import React from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  publishedTime?: string;
  modifiedTime?: string;
}

const SEO: React.FC<SEOProps> = ({ 
  title, 
  description, 
  keywords = '',
  image = '',
  url = '',
  type = 'website',
  publishedTime = '',
  modifiedTime = ''
}) => {
  const siteName = "GuangzhouHub";
  const defaultImage = "https://guangzhouhub.com/og-image.jpg";
  const siteUrl = "https://guangzhouhub.com";
  const twitterHandle = "@guangzhouhub";
  
  const metaTitle = `${title} | ${siteName}`;
  const metaDescription = description;
  const metaImage = image || defaultImage;
  const metaUrl = url || siteUrl;
  const metaKeywords = keywords || 'Guangzhou, Canton, travel, tourism, attractions, Guangdong, Pearl River Delta,广州,旅游,景点,广东,珠江三角洲';
  
  // 结构化数据 - 旅游网站
  const getStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@type": "TravelAgency",
      "name": siteName,
      "description": "Discover Guangzhou (Canton), the capital of Guangdong province and a major commercial hub in southern China with rich Cantonese culture",
      "url": siteUrl,
      "logo": "https://guangzhouhub.com/logo.png",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Guangdong",
        "addressCountry": "CN"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "info@guangzhouhub.com"
      },
      "sameAs": [
        "https://twitter.com/guangzhouhub"
      ]
    };
    
    // 如果是文章页面
    if (type === 'article') {
      const articleData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": metaTitle,
        "description": metaDescription,
        "image": metaImage,
        "author": {
          "@type": "Organization",
          "name": siteName
        },
        "publisher": {
          "@type": "Organization",
          "name": siteName,
          "logo": {
            "@type": "ImageObject",
            "url": "https://guangzhouhub.com/logo.png"
          }
        },
        "datePublished": publishedTime || new Date().toISOString(),
        "dateModified": modifiedTime || new Date().toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": metaUrl
        }
      };
      
      return articleData;
    }
    
    return baseData;
  };

  return (
    <Helmet>
      {/* 基础meta标签 */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      
      {/* Open Graph (Facebook) */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      
      {/* 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>
      
      {/* 规范链接 */}
      <link rel="canonical" href={metaUrl} />
      
      {/* 其他meta标签 */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta charSet="utf-8" />
      <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
      <meta name="theme-color" content="#3b82f6" />
      
      {/* 网站图标 */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/site.webmanifest" />
    </Helmet>
  );
};

export default SEO;